import { useEffect, useState } from "react";

import { formatUsd } from "../../shared/money";
import type { PublicPress } from "../../shared/types";
import { LoadingState } from "../components/LoadingState";
import { ShareButtons } from "../components/ShareButtons";
import { getPress, getPublicState } from "../lib/api";

function duration(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hours`;
  return `${Math.floor(hours / 24)} days`;
}

export function PressPage({ pressNumber }: { pressNumber: number }) {
  const [press, setPress] = useState<PublicPress | null>(null);
  const [currentPriceCents, setCurrentPriceCents] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([getPress(pressNumber, controller.signal), getPublicState(controller.signal)])
      .then(([pressData, state]) => {
        setPress(pressData);
        setCurrentPriceCents(state.currentPriceCents);
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "Press not found.");
      });
    return () => controller.abort();
  }, [pressNumber]);

  if (error) {
    return (
      <main className="centered-page">
        <h1>PRESS NOT FOUND.</h1>
        <p>{error}</p>
        <a className="text-link" href="/history">
          RETURN TO HISTORY
        </a>
      </main>
    );
  }
  if (!press || currentPriceCents === null) {
    return (
      <main className="centered-page">
        <LoadingState label="Reading this press" />
      </main>
    );
  }

  const url = `${window.location.origin}/press/${press.pressNumber}`;
  const shareText = press.isCurrentHolder
    ? `I paid ${formatUsd(press.amountSubtotalCents)} to press the button on dontpress.lol. I'm Press #${press.pressNumber} and currently control the homepage and its ${press.themeColor.toUpperCase()} color theme. The next minimum is ${formatUsd(currentPriceCents)}. Please don't replace me.`
    : `I became Press #${press.pressNumber} on dontpress.lol and chose ${press.themeColor.toUpperCase()} for the site. My press is permanently recorded in the history. The button now starts at ${formatUsd(currentPriceCents)}.`;

  return (
    <main className="content-page press-detail-page">
      <article className="press-record">
        <p className="placement-label">
          {press.isCurrentHolder
            ? "CURRENT HOLDER · PAID FEATURED PLACEMENT"
            : "PERMANENT HISTORY PAGE"}
        </p>
        <p className="press-record-number">PRESS #{press.pressNumber}</p>
        <h1>{press.displayName}</h1>
        {press.contentStatus !== "visible" ? (
          <p className="moderation-notice">
            Content removed for violating the Acceptable Use Policy.
          </p>
        ) : press.publicMessage ? (
          <p className="press-record-message">{press.publicMessage}</p>
        ) : null}
        <dl className="press-facts">
          <div>
            <dt>PAID</dt>
            <dd>{formatUsd(press.amountSubtotalCents)}</dd>
          </div>
          <div>
            <dt>FIRST ACTIVATED</dt>
            <dd>
              {new Intl.DateTimeFormat("en", { dateStyle: "long", timeStyle: "short" }).format(
                press.firstActivatedAt,
              )}
            </dd>
          </div>
          <div>
            <dt>TOTAL TIME AS HOLDER</dt>
            <dd>{duration(press.totalHolderDurationMs)}</dd>
          </div>
          <div>
            <dt>AGGREGATED LINK CLICKS</dt>
            <dd>{press.outboundClickCount}</dd>
          </div>
          <div>
            <dt>SITE COLOR</dt>
            <dd className="theme-color-value">
              <span style={{ backgroundColor: press.themeColor }} aria-hidden="true" />
              {press.themeColor.toUpperCase()}
            </dd>
          </div>
        </dl>
        {press.destinationHostname ? (
          <a
            className="destination-link"
            href={`/go/${press.pressNumber}`}
            target="_blank"
            rel="nofollow ugc sponsored noopener noreferrer"
          >
            VISIT {press.destinationHostname.toUpperCase()}
          </a>
        ) : null}
      </article>
      <ShareButtons text={shareText} url={url} pressNumber={press.pressNumber} />
    </main>
  );
}
